import { MAIN_API_ROOT } from 'app_config';
import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { getAccessToken, getDeviceId } from 'helpers/tokenManager';
import { getLanguageCode } from 'helpers/translationHelper';
import type { JwtPayload } from 'jwt-decode';
import jwtDecode from 'jwt-decode';

import { refreshToken } from './tokenRefresher';

export const EXPORT_TIMEOUT = 45000; // 45s
export const SUPERADMIN_EXPORT_TIMEOUT = 120000; // 120s
const DEFAULT_TIMEOUT = 15000;
const TOKEN_REFRESH_THRESHOLD = 0.2; // 20%

export const MISSING_TOKEN_ERROR_KEY = 'missingToken';
export const REFRESH_FAILED_ERROR_KEY = 'refresh_failed';

const createProtectedApiClient = (apiRoot: string) => {
  const axiosInstance: AxiosInstance = axios.create();
  axiosInstance.defaults.timeout = DEFAULT_TIMEOUT;

  axiosInstance.interceptors.request.use(async (cfg) => {
    const paramConfig = cfg;
    // ensure headers object exists
    paramConfig.headers = axios.AxiosHeaders.from(paramConfig.headers || {});

    let token = getAccessToken();
    if (!token) return Promise.reject(new Error(MISSING_TOKEN_ERROR_KEY));

    try {
      const decodedJwt = jwtDecode<JwtPayload>(token);

      if (decodedJwt?.exp && decodedJwt?.iat) {
        const lifetimeThreshold = (decodedJwt.exp - decodedJwt.iat) * TOKEN_REFRESH_THRESHOLD;
        const refreshAtMs = (decodedJwt.exp - lifetimeThreshold) * 1000;
        const now = Date.now();

        if (now >= refreshAtMs) {
          const refreshResult = await refreshToken();
          const newToken = refreshResult?.accessToken || getAccessToken();

          if (newToken) {
            token = newToken;
            paramConfig.headers.set('Authorization', `Bearer ${token}`);
            paramConfig.headers.set('deviceId', getDeviceId());
            paramConfig.headers.set('X-Accept-ProblemDetails', true);
            paramConfig.headers.set('Accept-Language', getLanguageCode() ?? 'cs');
          } else {
            return Promise.reject(new Error(REFRESH_FAILED_ERROR_KEY));
          }
        } else {
          paramConfig.headers.set('Authorization', `Bearer ${token}`);
          paramConfig.headers.set('deviceId', getDeviceId());
          paramConfig.headers.set('X-Accept-ProblemDetails', true);
          paramConfig.headers.set('Accept-Language', getLanguageCode() ?? 'cs');
        }
      } else {
        // token has no timing info; attach as-is
        paramConfig.headers.set('Authorization', `Bearer ${token}`);
        paramConfig.headers.set('deviceId', getDeviceId());
        paramConfig.headers.set('X-Accept-ProblemDetails', true);
        paramConfig.headers.set('Accept-Language', getLanguageCode() ?? 'cs');
      }
    } catch {
      // jwtDecode can throw for malformed tokens; attach the token anyway and continue
      paramConfig.headers.set('Authorization', `Bearer ${token}`);
      paramConfig.headers.set('deviceId', getDeviceId());
      paramConfig.headers.set('X-Accept-ProblemDetails', true);
      paramConfig.headers.set('Accept-Language', getLanguageCode() ?? 'cs');
    }

    if (!paramConfig.baseURL) {
      paramConfig.baseURL = apiRoot || MAIN_API_ROOT;
    }

    return paramConfig;
  });

  return axiosInstance;
};

export default createProtectedApiClient;
